import { cn } from "@/lib/utils";
import { Loader2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Header "Indietro" condiviso: torna alla pagina precedente (Home se aperta da Azione rapida),
// con fallback alla Home quando non c'è cronologia. Riutilizzabile su tutte le pagine.
export function BackHeader({ label = "Indietro", className, testId = "back-button" }) {
  const navigate = useNavigate();
  const goBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) navigate(-1);
    else navigate("/");
  };
  return (
    <button type="button" data-testid={testId} onClick={goBack} className={cn("mb-3 flex items-center gap-2 text-foreground", className)}>
      <ArrowLeft className="h-5 w-5" />
      <span className="font-extrabold">{label}</span>
    </button>
  );
}

export function Eyebrow({ children, className }) {
  return (
    <p className={cn("text-[11px] font-extrabold tracking-widest text-primary", className)}>
      {children}
    </p>
  );
}

export function PageTitle({ children, className }) {
  return (
    <h1 className={cn("text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight", className)}>
      {children}
    </h1>
  );
}

export function PageSubtitle({ children, className }) {
  return <p className={cn("text-sm text-muted-foreground leading-relaxed mt-1", className)}>{children}</p>;
}

export function SectionTitle({ children, className }) {
  return <h2 className={cn("text-lg font-extrabold text-foreground", className)}>{children}</h2>;
}

export function Loader({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground" data-testid="loader">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
      {label ? <span className="text-sm">{label}</span> : null}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, action, testId }) {
  return (
    <div
      className="flex flex-col items-center justify-center text-center gap-3 rounded-2xl border border-dashed border-border bg-card/60 py-16 px-6"
      data-testid={testId || "empty-state"}
    >
      {Icon ? <Icon className="h-10 w-10 text-muted-foreground" /> : null}
      <p className="text-lg font-extrabold text-foreground">{title}</p>
      {text ? <p className="text-sm text-muted-foreground max-w-md leading-relaxed">{text}</p> : null}
      {action || null}
    </div>
  );
}

const STATUS_STYLES = {
  success: "bg-[hsl(var(--success))] text-white",
  neutral: "bg-secondary text-secondary-foreground",
  warning: "bg-[hsl(var(--warning))] text-white",
  danger: "bg-destructive text-white",
};

export function StatusBadge({ children, tone = "neutral", testId }) {
  return (
    <span
      data-testid={testId}
      className={cn("inline-flex items-center rounded-full px-3 py-1 text-[11px] font-extrabold", STATUS_STYLES[tone])}
    >
      {children}
    </span>
  );
}

export function Card({ children, className, ...props }) {
  return (
    <div
      className={cn("rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-sm", className)}
      {...props}
    >
      {children}
    </div>
  );
}


// Riepilogo tratta condiviso: CARICO e SCARICO impilati (mai affiancati), con
// Nome azienda/deposito – Comune (Provincia). Nessun indirizzo stradale completo.
// Usato in tutte le liste di viaggi (Azienda, Vettore, Autista) per uniformità.
function cityProv(place) {
  if (!place || !place.comune) return "";
  return place.provincia ? `${place.comune} (${place.provincia})` : place.comune;
}
// Nome sintetico per le liste: primo segmento prima della virgola (nome azienda/deposito),
// mai l'indirizzo stradale completo.
function shortName(raw) {
  const text = (raw || "").trim();
  if (!text) return "";
  return text.split(",")[0].trim();
}
function RouteLine({ label, tone, name, city }) {
  return (
    <div className="flex items-start gap-2">
      <span className={cn("mt-0.5 shrink-0 rounded-md px-2 py-0.5 text-[11px] font-extrabold", tone)}>{label}</span>
      <p className="min-w-0 flex-1 break-words text-[15px] font-extrabold text-foreground">
        <span className="line-clamp-1">{name}</span>
        {city ? <span className="block text-[13px] font-semibold text-muted-foreground">{city}</span> : null}
      </p>
    </div>
  );
}
export function TripRoute({ trip, className }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <RouteLine label="Carico" tone="bg-secondary text-secondary-foreground" name={shortName(trip.pickup_place?.name || trip.pickup)} city={cityProv(trip.pickup_place)} />
      <RouteLine label="Scarico" tone="bg-primary/10 text-primary" name={shortName(trip.delivery_place?.name || trip.delivery)} city={cityProv(trip.delivery_place)} />
    </div>
  );
}
